import type { Doc } from "building-types/doc";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import {
  entitiesEligibleForEntitySettings,
  type EligibleEntitySettingEntity,
} from "building-types/entitySettings/entitySettingsTypes";
import type { SettingsSetHandle } from "building-types/handles/docEntityHandleTypes";
import type { EntitySettingsEligibleHandle } from "building-types/handles/entitySettingsEligibleHandle";
import type { SettingsSet } from "building-types/settings/settingsSet";
import { SettingsSetBehaviorType } from "building-types/settings/settingsSetBehaviorType";
import type { RWTable } from "common/data/table";
import { docGetEntityHandleById } from "./entity/docGetEntityHandleById";
import { docGetEntityForEntitySetting } from "./entity/docGetEntitySettings";
import { docGetTableEntity } from "./entity/docGetTableEntity";
import { entityIsTargetedSettingsSet } from "./settingsTools";

export function docGetEntitiesReferencingTargetedSettingsSet(doc: Doc, handle: SettingsSetHandle) {
  const settingsSet = docGetTableEntity(doc, handle);

  if (settingsSet?.type !== SettingsSetBehaviorType.TARGETED) {
    return {};
  }

  const entitySettingsReferencingSettingsSet: RWTable<{
    entity: EligibleEntitySettingEntity;
    handle: EntitySettingsEligibleHandle;
  }> = {};

  for (const id in doc.entitySettings) {
    const entitySetting = doc.entitySettings[id];
    const referencedEntity = docGetEntityForEntitySetting(doc, { type: "ENTITY_SETTINGS", id });

    if (!referencedEntity) {
      continue;
    }

    const referencedEntityHandle = docGetEntityHandleById(
      doc,
      referencedEntity.id,
      Object.keys(entitiesEligibleForEntitySettings) as DocEntityType[],
    ) as EntitySettingsEligibleHandle;

    if (!referencedEntityHandle) {
      continue;
    }

    const setting = doc.settings[entitySetting.settingsOrSettingsSetId];

    if (setting?.parentId === settingsSet?.id) {
      entitySettingsReferencingSettingsSet[referencedEntity.id] = {
        entity: referencedEntity,
        handle: referencedEntityHandle,
      };
      continue;
    }

    if (entitySetting.settingsOrSettingsSetId !== settingsSet?.id) {
      continue;
    }

    if (!referencedEntityHandle) {
      continue;
    }

    entitySettingsReferencingSettingsSet[referencedEntity.id] = {
      entity: referencedEntity,
      handle: referencedEntityHandle,
    };
  }

  return entitySettingsReferencingSettingsSet;
}

export function docGetSettingsSetByBehaviorType(doc: Doc) {
  const globalSettingsSets: RWTable<SettingsSet> = {};
  const targetedSettingsSets: RWTable<SettingsSet> = {};

  for (const id in doc.settingsSets) {
    const settingsSet = doc.settingsSets[id];

    if (entityIsTargetedSettingsSet(settingsSet)) {
      targetedSettingsSets[id] = settingsSet;
    } else {
      globalSettingsSets[id] = settingsSet;
    }
  }

  return { globalSettingsSets, targetedSettingsSets };
}
